import React from 'react';
import axios from 'axios';

import { targetType } from 'contexts';

import { SearchOrgs } from 'api/SearchController/orgs';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import { FormattedOrgForAppointmentDeskType, SearchType } from './types';
import assume from './assume';
import { BrokerageInfo, SubscriptionTag } from './components';
import { StyledButton } from './StyledComponents';
import { MLS_TYPE_LABEL_MAP } from './constants';

const MY_BROKERAGE_PATH = '/listing/list/brokerage/0';

const crossOrgAppointmentDeskOrgSearch = (
  isUserMlsStaff: boolean,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
  answeringConfirm: (target: targetType, cb: () => void) => () => void,
) => {
  const searchColumns: SearchType<FormattedOrgForAppointmentDeskType>['columns'] =
    [
      {
        dataIndex: 'orgInfo',
        key: 'orgInfo',
        width: 400,
        render: (orgInfo: FormattedOrgForAppointmentDeskType['orgInfo']) => (
          <BrokerageInfo orgName={orgInfo.name} orgLogoUrl={orgInfo.logoUrl} />
        ),
      },
      {
        dataIndex: 'oids',
        key: 'oids',
        ellipsis: {
          showTitle: false,
        },
        render: (oids: FormattedOrgForAppointmentDeskType['oids']) => (
          <Tooltip placement="topLeft" title={oids}>
            {oids}
          </Tooltip>
        ),
      },
      {
        dataIndex: 'mlsTypes',
        key: 'mlsTypes',
        width: 150,
        render: (mlsTypes: FormattedOrgForAppointmentDeskType['mlsTypes']) =>
          Array.isArray(mlsTypes) &&
          mlsTypes
            .map(mlsType => MLS_TYPE_LABEL_MAP[mlsType] ?? mlsType)
            .join(', '),
      },
      {
        dataIndex: 'subscriptionTag',
        key: 'subscriptionTag',
        width: 150,
        render: (
          subscriptionTag: FormattedOrgForAppointmentDeskType['subscriptionTag'],
        ) => (
          <SubscriptionTag
            subscriptionTag={subscriptionTag}
            toolTipTextType="brokerage"
          />
        ),
      },
      {
        align: 'center',
        dataIndex: 'id',
        key: 'Button',
        width: 150,
        render: (id: string, { auth, subscriptionTag }) => (
          <StyledButton
            onClick={answeringConfirm(
              { type: 'brokerage', subscriptionTag },
              () => assume(id, Boolean(auth.isAssumedNotBB), MY_BROKERAGE_PATH),
            )}
            data-cy="brokerage-assume-button"
          >
            <Icon type="antdLoginOutlined" /> Assume
          </StyledButton>
        ),
      },
    ];
  const search: SearchType<FormattedOrgForAppointmentDeskType> = {
    columns: searchColumns.filter(Boolean),
    search: async (
      debouncedQuery,
      forceBlur,
      { auth, mlsOptionsCount, mlsFilter },
    ) => {
      let mlsTypes: string[] = [];
      if (mlsFilter && mlsFilter.length !== mlsOptionsCount)
        mlsTypes = mlsFilter;

      const {
        data: { orgs },
      } = await axios.get<SearchOrgs>('/search/orgs', {
        params: {
          input: debouncedQuery,
          ...(mlsTypes.length ? { mlsTypes } : {}),
        },
      });

      return orgs.map((org, i) => ({
        key: i,
        id: org.id,
        orgInfo: {
          logoUrl: org.logoUrl,
          name: org.name,
        },
        oids: org.oids.join(', '),
        subscriptionTag: org.subscriptionTag,
        mlsTypes: org.mlsTypes,
        auth,
      }));
    },
    onConfirm: (itemSelected: SearchOrgs['orgs'][number], { auth }) => {
      const { id } = itemSelected;
      const isAssumed = Boolean(auth.isAssumedNotBB);
      assume(id, isAssumed, MY_BROKERAGE_PATH);
    },
    createOnRow: () => (item, index) => ({
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
      'data-cy': `universal-brokerage-item-${item.id}`,
    }),
  };

  return search;
};

export { crossOrgAppointmentDeskOrgSearch };
